import React, { useRef, useState, useEffect } from 'react';
import { Share2, Download, X, Sparkles, Award, MapPin, Clock, Footprints } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareWalkCard
 * Renders a beautiful "I just walked..." social card and exposes Share / Save / Copy actions.
 * 
 * Props:
 *   tour       — { title, city, cover_image_url? }
 *   stats      — { stops, distanceText?, durationText?, stridesEarned? }
 *   achievements — array of unlocked badges to show (max 3)
 *   userName   — display name on card
 *   tourUrl    — public tour URL to share
 *   onClose    — close handler
 *   onShared   — callback invoked after successful share (to award Strides)
 * 
 * The card is rendered with HTML/CSS and converted to a PNG via the html-to-image
 * library when the user taps Share or Save. This keeps it visually rich AND
 * sharable across every platform via Web Share API. No platform-specific code.
 */
const ShareWalkCard = ({
  tour,
  stats = {},
  achievements = [],
  userName = 'Traveler',
  tourUrl = '',
  onClose,
  onShared,
}) => {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [libReady, setLibReady] = useState(false);
  const [htmlToImage, setHtmlToImage] = useState(null);

  // Lazy-load html-to-image so it doesn't bloat first-paint
  useEffect(() => {
    let mounted = true;
    import('html-to-image').then((mod) => {
      if (mounted) {
        setHtmlToImage(mod);
        setLibReady(true);
      }
    }).catch(() => {
      // Library missing — fall back to text-only share
      if (mounted) setLibReady(true);
    });
    return () => { mounted = false; };
  }, []);

  const generatePng = async () => {
    if (!htmlToImage || !cardRef.current) return null;
    const blob = await htmlToImage.toBlob(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#0F172A',
    });
    return blob;
  };

  const shareText = `I just walked the ${tour?.title || 'Audiostride tour'}${tour?.city ? ` in ${tour.city}` : ''}! 🚶✨`;

  const handleShare = async () => {
    setGenerating(true);
    try {
      const blob = await generatePng();
      const file = blob ? new File([blob], 'audiostride-walk.png', { type: 'image/png' }) : null;
      const shareData = {
        title: 'My Audiostride Walk',
        text: shareText,
        url: tourUrl || window.location.origin,
      };
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        shareData.files = [file];
      }
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Shared! +Strides incoming 🎉');
        if (onShared) onShared();
      } else {
        // Fallback: copy link
        await navigator.clipboard.writeText(`${shareText} ${tourUrl}`);
        toast.success('Link copied to clipboard');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error('Could not share — try Save Image instead');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const blob = await generatePng();
      if (!blob) throw new Error('PNG render failed');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audiostride-${(tour?.title || 'walk').replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Image saved');
    } catch {
      toast.error('Could not save image');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(tourUrl || window.location.origin);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const visibleAchievements = (achievements || []).slice(0, 3);

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex flex-col items-center justify-start sm:justify-center p-4 overflow-y-auto" data-testid="share-walk-overlay">
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        data-testid="share-walk-close"
        aria-label="Close"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="w-full max-w-sm mt-12 sm:mt-0">
        <h2 className="text-white text-center font-black text-2xl mb-1 flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-300" />
          Share your walk
        </h2>
        <p className="text-slate-300 text-center text-sm mb-4">+10 Strides for every share 🎉</p>

        {/* The social card */}
        <div
          ref={cardRef}
          data-testid="share-walk-card"
          className="rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: 'linear-gradient(135deg, #0F172A 0%, #134E4A 50%, #0E7490 100%)' }}
        >
          {/* Cover image */}
          <div className="relative h-44 overflow-hidden">
            {tour?.cover_image_url ? (
              <img
                src={tour.cover_image_url}
                alt={tour.title}
                className="absolute inset-0 w-full h-full object-cover"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 via-rose-400 to-violet-500" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 text-white">
              <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-teal-300">{userName} just walked</p>
              <h3 className="text-2xl font-black leading-tight drop-shadow-lg">{tour?.title || 'an Audiostride tour'}</h3>
              {tour?.city && (
                <p className="text-sm text-white/90 flex items-center gap-1 mt-1">
                  <MapPin className="w-3.5 h-3.5" /> {tour.city}
                </p>
              )}
            </div>
          </div>

          {/* Stats row */}
          <div className="px-5 py-5 grid grid-cols-3 gap-2">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-teal-300">
                <Footprints className="w-4 h-4" />
              </div>
              <p className="text-white font-black text-xl leading-tight">{stats.stops ?? 0}</p>
              <p className="text-slate-400 text-[10px] uppercase tracking-wide font-bold">Stops</p>
            </div>
            {stats.distanceText && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-teal-300">
                  <MapPin className="w-4 h-4" />
                </div>
                <p className="text-white font-black text-xl leading-tight">{stats.distanceText}</p>
                <p className="text-slate-400 text-[10px] uppercase tracking-wide font-bold">Distance</p>
              </div>
            )}
            {stats.durationText && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-teal-300">
                  <Clock className="w-4 h-4" />
                </div>
                <p className="text-white font-black text-xl leading-tight">{stats.durationText}</p>
                <p className="text-slate-400 text-[10px] uppercase tracking-wide font-bold">Time</p>
              </div>
            )}
          </div>

          {/* Strides earned strip */}
          {stats.stridesEarned ? (
            <div className="mx-5 mb-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl px-4 py-2.5 flex items-center justify-between">
              <span className="text-slate-900 text-xs font-black uppercase tracking-wide">Strides earned</span>
              <span className="text-slate-900 font-black text-xl">+{stats.stridesEarned}</span>
            </div>
          ) : null}

          {/* Achievements */}
          {visibleAchievements.length > 0 && (
            <div className="px-5 pb-4">
              <p className="text-slate-400 text-[10px] uppercase tracking-[0.2em] font-bold mb-2 flex items-center gap-1">
                <Award className="w-3 h-3" /> Badges unlocked
              </p>
              <div className="flex gap-2 flex-wrap">
                {visibleAchievements.map((a) => (
                  <span key={a.achievement_id} className="bg-white/10 border border-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                    {a.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="bg-slate-900/80 px-5 py-3 flex items-center justify-between">
            <span className="text-slate-300 text-xs">audiostride.com</span>
            <span className="text-teal-300 text-xs font-bold">GPS Audio Tours</span>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={handleShare}
            disabled={generating || !libReady}
            data-testid="share-walk-share"
            className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white py-3 rounded-2xl font-bold flex items-center justify-center gap-2 active:scale-95 transition disabled:opacity-60 col-span-2 shadow-lg"
          >
            <Share2 className="w-5 h-5" />
            {generating ? 'Preparing…' : 'Share'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={generating || !libReady}
            data-testid="share-walk-download"
            className="bg-white/10 hover:bg-white/20 text-white py-3 rounded-2xl font-bold flex items-center justify-center transition disabled:opacity-60"
            aria-label="Save image"
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
        <button
          type="button"
          onClick={handleCopyLink}
          data-testid="share-walk-copy-link"
          className="w-full mt-2 text-slate-300 hover:text-white text-sm py-2 transition"
        >
          Or copy link
        </button>
      </div>
    </div>
  );
};

export default ShareWalkCard;
